import { z } from 'zod'

import { ApiSchema } from '../api-response'
import { encodedClaimsSchema } from '../auth-claims'
import { PlatformWidePermission } from '../auth-claims/permissions'
import { defineApi } from '../http'
import { oneItem } from '../shared'
import { roleTypeSchema } from './roletype'

/**
 * Api schema for the Panorama and billing claims endpoint.  Generally speaking,
 * if you're looking to get claims, you should use the claims-helpers
 * package.
 *
 * @group Routes
 *
 * @route
 * `GET /internal/claims/{username}` (internal route for Panorama claims)
 *
 * @route
 * `GET /encodedclaims/{username}` (authorizer route for Panorama claims)
 *
 * @route
 * `GET /internal/billing/claims/{username}` (internal route for billing claims)
 *
 * @apiParam {username} panorama username
 */
export const getClaimsSchema = new ApiSchema(encodedClaimsSchema)

const organizationsClaimsSchema = z.object({
  admin: z.boolean(),
  support: z.boolean(),
  directRole: roleTypeSchema.nullable(),
  viaDealer: z.boolean()
})

export type OrganizationsClaims = z.infer<typeof organizationsClaimsSchema>

/**
 * Returns a user's relationship to a single organization: their direct role in
 * the org (if any) and whether they reach the org through a dealer relationship.
 * Direct and dealer access are reported independently, so a user may have a
 * `directRole` and `viaDealer` set at the same time.
 */
export const organizationsClaimsApi = defineApi({
  pathSchema: z.object({
    username: z.string(),
    orgCode: z.string()
  }),
  pathFor: ({ username, orgCode }) => `/claims/${username}/organizations/${orgCode}`,
  name: 'GetOrganizationsClaims',
  description: "Retrieve a user's direct role and dealer relationship for a single organization",
  requiredPermissions: PlatformWidePermission.PerformBasicUserActions,
  responseSchema: oneItem(organizationsClaimsSchema),
  gatewayPath: '/claims/:username/organizations/:orgCode' as const,
  queryKey: (p) => [p.username, p.orgCode, 'org-claims']
})
